import {
  ExtractFnReturnType,
  MutationConfig,
  QueryConfig,
  queryClient,
} from "@/lib/react-query";
import {
  Project,
  ProjectSchema,
  ProjectWithRequestsResponse,
  ProjectWithRequestsSchema,
  RequestCreateProject,
  RequestUpdateProject,
} from "@/types/project";
import { client } from "@/utils/axios.config";
import i18next from "@/utils/i18n";
import { useMutation, useQuery } from "@tanstack/react-query";
import { HttpStatusCode } from "axios";
import { NavigateFunction } from "react-router-dom";
import { toast } from "react-toastify";
import { z } from "zod";

// getProjects
export const getProjects = async (): Promise<Project[]> => {
  return z.array(ProjectSchema).parse(await client.get(`/api/project`));
};

type QueryFnTypeGetProjects = typeof getProjects;

type UseProjectsOptions = {
  config?: QueryConfig<QueryFnTypeGetProjects>;
};

export const useProjects = ({ config = {} }: UseProjectsOptions) => {
  return useQuery<ExtractFnReturnType<QueryFnTypeGetProjects>>({
    ...config,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, //not refetching for the next 10 minutes
    queryKey: ["projects"],
    queryFn: () => getProjects(),
  });
};

// updateProject
export const updateProject = async ({
  projectId,
  name,
  favorite,
  description,
}: {
  projectId: string;
  name: string;
  favorite: boolean;
  description: string;
}): Promise<Project> => {
  const request: RequestUpdateProject = {
    name: name,
    favorite: favorite,
    description: description,
  };
  return ProjectSchema.parse(
    await client.put(`/api/project/${projectId}`, request)
  );
};

type UseUpdateProjectOptions = {
  close?: () => void;
  config?: MutationConfig<typeof updateProject>;
};

export const useUpdateProject = ({
  config,
  close,
}: UseUpdateProjectOptions = {}) => {
  return useMutation({
    onError: (apiError) => {
      if (HttpStatusCode.NotFound === apiError?.response?.status) {
        toast.error(i18next.t("toast.project.update.error.notFound"), {
          toastId: "useUpdateProject.NotFound",
        });
      }
      if (HttpStatusCode.Conflict === apiError?.response?.status) {
        toast.error(i18next.t("toast.project.update.error.conflict"), {
          toastId: "useUpdateProject.Conflict",
        });
      }
    },
    onSuccess: () => {
      toast.success(i18next.t("toast.project.update.success"), {
        toastId: "useUpdateProject.success",
      });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      if (close !== undefined) {
        close();
      }
    },
    ...config,
    mutationFn: updateProject,
  });
};

//deleteProject
export const deleteProject = ({
  projectId,
}: {
  projectId: string;
}): Promise<void> => {
  return client.delete(`/api/project/${projectId}`);
};

type UseDeleteProjectOptions = {
  navigate?: NavigateFunction;
  config?: MutationConfig<typeof deleteProject>;
};

export const useDeleteProject = ({
  config,
  navigate,
}: UseDeleteProjectOptions) => {
  return useMutation({
    onError: (apiError) => {
      if (HttpStatusCode.NotFound === apiError?.response?.status) {
        toast.error(i18next.t("toast.project.delete.error.notFound"), {
          toastId: "useDeleteProject.NotFound",
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      if (navigate) {
        navigate("/profile");
      }
    },
    ...config,
    mutationFn: deleteProject,
  });
};

//createProject
export const createProject = async ({
  name,
  description,
}: {
  name: string;
  description: string;
}): Promise<Project> => {
  const request: RequestCreateProject = {
    name: name,
    description: description,
  };
  return ProjectSchema.parse(await client.post(`/api/project`, request));
};

type UseCreateProjectOptions = {
  navigate: NavigateFunction;
  config?: MutationConfig<typeof createProject>;
};

export const useCreateProject = ({
  config,
  navigate,
}: UseCreateProjectOptions) => {
  return useMutation({
    onError: (apiError) => {
      if (HttpStatusCode.NotFound === apiError?.response?.status) {
        toast.error(i18next.t("toast.project.create.error.notFound"), {
          toastId: "useCreateProject.NotFound",
        });
      }
      if (HttpStatusCode.Conflict === apiError?.response?.status) {
        toast.error(i18next.t("toast.project.create.error.conflict"), {
          toastId: "useCreateProject.Conflict",
        });
      }
    },
    onSuccess: (response) => {
      toast.success(i18next.t("toast.project.create.success"), {
        toastId: "success2",
      });
      const url = `/project/${response?.id}`;

      queryClient.invalidateQueries({ queryKey: ["projects"] });
      navigate(url);
    },
    ...config,
    mutationFn: createProject,
  });
};

//getProject
export const getProject = async (
  projectId: string
): Promise<ProjectWithRequestsResponse> => {
  return ProjectWithRequestsSchema.parse(
    await client.get(`/api/project/${projectId}`)
  );
};

type QueryFnTypeGetProject = typeof getProject;

type UseProjectOptions = {
  projectId: string;
  config?: QueryConfig<QueryFnTypeGetProject>;
};

export const useProject = ({ projectId, config = {} }: UseProjectOptions) => {
  return useQuery<ExtractFnReturnType<QueryFnTypeGetProject>>({
    ...config,
    refetchOnWindowFocus: false,
    queryKey: ["projects", projectId],
    queryFn: () => getProject(projectId),
  });
};
